import React, { useContext, useEffect, useMemo, useState } from "react";

import { animated, Spring } from "@react-spring/web";

import {
  Assessment,
  MultipleChoiceQuestion,
  Option,
  Question,
  TextQuestion,
} from "src/types/coaching/courses";

import _ from "lodash";
import cx from "classnames";

import Button from "src/components/button";
import TextareaAutosize from "react-autosize-textarea";
import Tooltip from "src/components/tooltip";

import { CourseContext } from "../../../../context";
import { AssessmentReport, LearningRecordCourse, QuestionReport } from "src/types/coaching/learners";
import { Carousel } from "react-bootstrap";
import { QUESTION_TYPES } from "src/constants/coaching";
import useMeasure from "react-use-measure";
import { ElementTimerComponent } from "src/components/elementTimer";

import styles from "./view.module.scss";

interface AssessmentViewProps {
  course: LearningRecordCourse;
  assessment: Assessment
}

export const AssessmentView: React.FC<AssessmentViewProps> = ({ course, assessment }) => {
  const courseContext = useContext(CourseContext);

  const { session, questionsAnswered, completed, status, report } = useMemo(() => {
    const session = courseContext.courseContentSession?.session;
    const questionsAnswered = _.size(_.get(courseContext.courseContentSession, "questions")) || 0;

    const completed = _.get(courseContext.courseContentSession, "completed") || false;
    const status = _.get(courseContext.courseContentSession, "status");
    const report = _.get(courseContext.courseContentSession, "report");

    return {
      session,
      questionsAnswered,
      completed,
      status,
      report
    };
  }, [courseContext.courseContentSession]);

  const handleRecordAnswerForQuestion = (question: string, answer: string) => {
    if (courseContext.recordAnswerForQuestion) {
      courseContext.recordAnswerForQuestion(assessment, question, answer);
    }
  }

  const handleSubmitAnswer = (question: string, answer: string, duration: number) => {
    if (courseContext.recordQuestionSession) {
      courseContext.recordQuestionSession(assessment, question, answer, duration);
    }
  }

  const handleUpdateSession = (duration: number) => {
    const questions = _.size(assessment.questions);
    const progress = questions > 0 ? questionsAnswered / questions : 0;

    if (courseContext.updateCourseContentSession && session) {
      courseContext.updateCourseContentSession(assessment, session, duration, progress);
    }
  }

  const handleSubmitAssessment = (duration: number) => {
    const questions = _.size(assessment.questions);
    const progress = questions > 0 ? questionsAnswered / questions : 0;

    if (courseContext.completeCourseContentSession && session) {
      courseContext.completeCourseContentSession(assessment, session, duration, progress);
    }
  }

  const handleViewReport = () => {
    if (courseContext.fetchAssessmentReport && session) {
      courseContext.fetchAssessmentReport(assessment, session);
    }
  }

  const handleEndAssessment = () => {
    if (courseContext.endAssessment) {
      courseContext.endAssessment(assessment);
    }
  }

  return (
    <div className={styles.container}>
      {status != "COMPLETED" && !completed && (
        <QuestionsContainer
          course={course}
          assessment={assessment}
          onAnswerChange={handleRecordAnswerForQuestion}
          onSubmitAnswer={handleSubmitAnswer}
          onUpdateSession={handleUpdateSession}
          onSubmitAssessment={handleSubmitAssessment}
          onCloseAssessment={handleEndAssessment}
        />
      )}

      {completed && !report &&
        <AssessmentCompletion
          course={course}
          assessment={assessment}
          onViewReport={handleViewReport}
          onEndAssessment={handleEndAssessment}
        />
      }

      {status == "COMPLETED" && report &&
        <AssessmentReportView
          course={course}
          assessment={assessment}
          report={report}
          onEndAssessment={handleEndAssessment}
        />
      }
    </div>
  );
};

interface QuestionsContainerProps {
  course: LearningRecordCourse;
  assessment: Assessment;
  // eslint-disable-next-line no-unused-vars
  onAnswerChange: (question: string, answer: string) => void;

  // eslint-disable-next-line no-unused-vars
  onSubmitAnswer: (question: string, answer: string, duration: number) => void;

  // eslint-disable-next-line no-unused-vars
  onSubmitAssessment: (duration: number) => void;

  // eslint-disable-next-line no-unused-vars
  onUpdateSession: (duration: number) => void;

  // eslint-disable-next-line no-unused-vars
  onCloseAssessment: () => void;
}

const QuestionsContainer: React.FC<QuestionsContainerProps> = ({ course, assessment, onAnswerChange, onSubmitAnswer, onUpdateSession, onSubmitAssessment, onCloseAssessment }) => {
  const durationRef = React.useRef<any>();
  const questionDurationRef = React.useRef<any>();

  const courseContext = React.useContext(CourseContext);

  const questions = useMemo(() => {
    return _.get(assessment, "questions") || [];
  }, [assessment]);

  const [currentQuestion, setCurrentQuestion] = useState(0);

  const question = useMemo(() => {
    return _.get(questions, `[${currentQuestion}]`);
  }, [questions, currentQuestion]);

  const { answer, completing } = useMemo(() => {
    const questions = _.get(courseContext.courseContentSession, "questions") || [];
    const questionItem = questions?.find((item) => item.question === question.id);

    const answer = _.get(questionItem, "answer") || "";
    const completing = _.get(courseContext.courseContentSession, "completing") || false;

    return {
      answer,
      completing
    }
  }, [courseContext.courseContentSession]);

  const handleAnswerChange = (question: string, answer: string) => {
    onAnswerChange(question, answer);
  }

  const handleSubmitAnswer = (question: string) => {
    const { duration } = questionDurationRef.current?.duration() || 0;
    // For question, we send the total duration spent on the question
    onSubmitAnswer(question, answer, duration);
  }

  const handleSubmitAssessment = () => {
    handleSubmitAnswer(question!.id!);

    const { change } = durationRef.current?.duration() || 0;
    // send only the change in duration and not the total duration. This is to avoid sending the same duration multiple times
    onSubmitAssessment(change);
  }

  const handleEndAssessment = () => {
    onCloseAssessment();
  }

  const handleDurationChange = (duration: number, change: number) => {
    console.log(`Time spent on ${assessment.id}: ${duration}, Change: ${change}`);
    onUpdateSession(change);
  }

  return (
    <ElementTimerComponent id={assessment.id!} ref={durationRef} onDurationChange={handleDurationChange}>
      <div className={styles.assessment}>
        <div className={styles.questionsContainer}>
          <div className={styles.header}>
            <div className={styles.questionsCount}>
              <div className={styles.currentQuestion}>{currentQuestion + 1}</div>
              <div className={styles.totalQuestions}>
                /{_.size(assessment.questions)} Questions
              </div>
            </div>
            <div className={styles.closeIcon} onClick={handleEndAssessment}>
              <i className="paperflite-close" />
            </div>
          </div>
          <AssessmentProgressIndicator
            totalQuestions={_.size(questions)}
            currentQuestion={currentQuestion}
          />

          <Carousel interval={null} activeIndex={currentQuestion} onSelect={(index) => setCurrentQuestion(index)}>
            {_.map(questions, (question, index) => {
              const activeQuestion = currentQuestion === index ? question.id! : "question";
              return (
                <Carousel.Item key={index}>
                  <ElementTimerComponent id={activeQuestion} ref={questionDurationRef}>
                    <QuestionContainer
                      key={index}
                      course={course}
                      assessment={assessment}
                      question={question}
                      onAnswerChange={handleAnswerChange}
                    />
                  </ElementTimerComponent>
                </Carousel.Item>
              )
            })}
          </Carousel>

          <div className={styles.actions}>
            {currentQuestion > 0 && (
              <Tooltip content={"Previous Question"} placement="left">
                <button
                  className={cx(styles.action, styles.previous)}
                  disabled={completing}
                  onClick={() => {
                    handleSubmitAnswer(question!.id!);
                    setCurrentQuestion(currentQuestion - 1)
                  }}
                >
                  <i className="paperflite-left-arrow"></i>
                </button>
              </Tooltip>
            )}
            {currentQuestion < _.size(questions) - 1 && (
              <Button
                className={cx(styles.action, styles.next)}
                variant="gradient"
                text="Next Question"
                icon="paperflite-right-arrow"
                disabled={!answer}
                onClick={() => {
                  handleSubmitAnswer(question!.id!);
                  setCurrentQuestion(currentQuestion + 1);
                }}
              />
            )}
            {currentQuestion == _.size(questions) - 1 && (
              <Button
                className={cx(styles.action, styles.next)}
                variant="gradient"
                text={completing ? "Submitting..." : "Submit Assessment"}
                icon="paperflite-tick"
                disabled={!answer || completing}
                onClick={handleSubmitAssessment}
              />
            )}
          </div>
        </div>
      </div>
    </ElementTimerComponent>
  );
};

interface AssessmentProgressIndicatorProps {
  totalQuestions: number;
  currentQuestion: number;
}

const AssessmentProgressIndicator: React.FC<AssessmentProgressIndicatorProps> = ({
  totalQuestions,
  currentQuestion,
}) => {
  return (
    <div className={cx(styles.progressIndicator, {
      [styles.linear]: totalQuestions > 10,
    })}>
      {_.times(totalQuestions, (index) => (
        <div key={index} className={cx(styles.question, {
          [styles.active]: index <= currentQuestion,
        })}
        />
      ))}
    </div>
  );
}

interface QuestionContainerProps {
  course: LearningRecordCourse;
  assessment: Assessment;
  question: Question;
  // eslint-disable-next-line no-unused-vars
  onAnswerChange: (question: string, answer: string) => void;
}

const QuestionContainer: React.FC<QuestionContainerProps> = ({
  course,
  assessment,
  question,
  onAnswerChange
}) => {

  const courseContext = useContext(CourseContext);

  const name = useMemo(() => {
    return _.get(question, "name");
  }, [question]);

  const answerType = useMemo(() => {
    return _.get(question, "type");
  }, [question]);

  const answer = useMemo(() => {
    const questions = _.get(courseContext.courseContentSession, "questions") || [];
    const questionItem = questions?.find((item) => item.question === question.id);

    return _.get(questionItem, "answer") || "";
  }, [courseContext.courseContentSession]);

  return (
    <div className={cx(styles.questionContainer)}>
      <div className={styles.nameField}>{name}</div>

      <div className={styles.answerField}>
        {answerType === "multiple_choice" && (
          <MultipleChoiceQuestionContainer
            question={question as MultipleChoiceQuestion}
            onAnswerChange={(answer) => {
              if (question.id) {
                onAnswerChange(question.id, answer);
              }
            }}
            currentAnswer={answer}
          />
        )}

        {answerType === "true_false" && (
          <MultipleChoiceQuestionContainer
            question={question as MultipleChoiceQuestion}
            onAnswerChange={(answer) => {
              if (question.id) {
                onAnswerChange(question.id, answer);
              }
            }}
            currentAnswer={answer}
          />
        )}

        {answerType === "text" && (
          <TextAnswer
            question={question as TextQuestion}
            onChange={(answer) => {
              if (question.id) {
                onAnswerChange(question.id, answer);
              }
            }}
            currentAnswer={answer}
          />
        )}
      </div>
    </div>
  );
};

interface MultipleChoiceQuestionContainerProps {
  question: MultipleChoiceQuestion;
  // eslint-disable-next-line no-unused-vars
  onAnswerChange: (answer: string) => void;
  currentAnswer?: string;
}

const MultipleChoiceQuestionContainer: React.FC<
  MultipleChoiceQuestionContainerProps
> = ({ question, onAnswerChange, currentAnswer }) => {


  const options = useMemo(() => {
    return _.get(question, "options") || [];
  }, [question]);

  const handleAnswerChange = (option: Option) => {
    onAnswerChange(option.uuid);
  };

  return (
    <div className={styles.multipleChoiceQuestionContainer}>
      <div className={styles.optionsContainer}>
        {_.map(options, (option, index) => (
          <OptionContainer
            key={index}
            option={option}
            selected={option.uuid === currentAnswer}
            onSelectionChange={(option) => {
              handleAnswerChange(option);
            }}
          />
        ))}
      </div>
    </div>
  );
};

interface OptionContainerProps {
  option: Option;
  selected: boolean;
  // eslint-disable-next-line no-unused-vars
  onSelectionChange: (option: Option) => void;
  // eslint-disable-next-line no-unused-vars
}

const OptionContainer: React.FC<OptionContainerProps> = ({
  option,
  selected,
  onSelectionChange,
}) => {
  const name = useMemo(() => {
    return _.get(option, "name");
  }, [option]);

  const handleSelectClicked = () => {
    onSelectionChange(option);
  };

  return (
    <div
      className={cx(styles.optionContainer, {
        [styles.selected]: selected,
      })}
      onClick={handleSelectClicked}
    >
      <div className={cx(styles.radio, {
        [styles.selectedRadio]: selected,
      })}
      >
        <i className={
          selected
            ? "paperflite-radio-button-selected"
            : "paperflite-radio-button-unselected"
        }
        ></i>
      </div>
      <div className={styles.optionText}>
        <span>{name}</span>
      </div>
    </div>
  );
};

interface TextAnswerProps {
  question: Question;
  // eslint-disable-next-line no-unused-vars
  onChange: (answer: string) => void;
  currentAnswer?: string;
}

const TextAnswer: React.FC<TextAnswerProps> = ({
  question,
  onChange,
  currentAnswer,
}) => {
  const [answer, setAnswer] = useState(currentAnswer || "");

  const handleAnswerChange = (e: any) => {
    setAnswer(e.target.value);
    onChange(e.target.value);
  };

  return (
    <TextareaAutosize
      className={styles.textAnswer}
      placeholder="Provide answer for the question..."
      rows={5}
      maxRows={10}
      value={answer}
      onChange={handleAnswerChange}
      onPointerEnterCapture={() => { }}
      onPointerLeaveCapture={() => { }}
    />
  );
};

interface AssessmentCompletionProps {
  course: LearningRecordCourse;
  assessment: Assessment;
  onViewReport: () => void;
  onEndAssessment: () => void;
}

const AssessmentCompletion: React.FC<AssessmentCompletionProps> = ({ course, assessment, onViewReport, onEndAssessment }) => {
  const courseContext = useContext(CourseContext);

  const { nextContent, nextIndex } = useMemo(() => {
    const contents = course.course.contents || [];
    const currentIndex = _.findIndex(contents, { id: assessment.id, type: "assessment" });
    if (currentIndex < 0 || currentIndex == contents.length - 1) {
      return {
        nextContent: null,
        nextIndex: null
      }
    };

    return {
      nextContent: contents[currentIndex + 1],
      nextIndex: currentIndex + 1
    };
  }, [course]);

  const fetchingReport = useMemo(() => {
    return _.get(courseContext.courseContentSession, "fetchingReport") || false;
  }, [courseContext.courseContentSession]);

  const buttonText = useMemo(() => {
    if (!nextContent) {
      return "Exit Assessment";
    }

    return nextContent?.type === "assessment" ? "Next Assessment" : "Next Lesson";
  }, [nextContent]);

  const handleEndAssessment = () => {
    if (nextIndex) {
      if (courseContext.onContentClicked) {
        courseContext.onContentClicked(nextIndex)
      }
    }

    onEndAssessment();
  }

  return (
    <div className={styles.congratulationsContainer}>
      <div className={styles.congratulationContent}>
        <img
          className={styles.trophyImage}
          src="/images/coaching/trophy.svg"
          alt="Trophy"
        />
        <h2 className={styles.congratsTitle}>Congratulations!</h2>
        <p className={styles.congratsMessage}>
          you&apos;ve successfully completed the assessment
        </p>

        <div className={styles.congratsButtons}>
          <Button
            variant="gradient"
            icon="paperflite-right-arrow"
            text={fetchingReport ? "Fetching Report..." : "View Report"}
            disabled={fetchingReport}
            onClick={onViewReport}
            className={styles.viewReportBtn}
          />
          <Button
            text={buttonText}
            onClick={handleEndAssessment}
            className={styles.exitBtn}
          />
        </div>
      </div>
    </div>
  );
}

interface AssessmentReportProps {
  course: LearningRecordCourse;
  assessment: Assessment;
  report: AssessmentReport;
  onEndAssessment: () => void;
}

const AssessmentReportView: React.FC<AssessmentReportProps> = ({ course, assessment, report, onEndAssessment }) => {

  const questions = useMemo(() => {
    return _.get(assessment, "questions") || [];
  }, [assessment]);

  const score = useMemo(() => {
    return ((report.score || 0) * 100).toFixed(0);
  }, [report]);

  return (
    <div className={styles.reportContainer}>
      <div className={styles.header}>
        <h3 className={styles.title}>Your Assessment report is here!</h3>
        <p className={styles.score}>here&apos;s your overall score for this assessment <span>{score}%</span></p>
        <div className={styles.closeIcon} onClick={onEndAssessment}>
          <i className="paperflite-close" />
        </div>
      </div>
      <div className={styles.panelContainer}>
        <div className={styles.questions}>
          {_.map(questions, (question, index) => (
            <QuestionAccordianPanel key={index}
              index={index}
              course={course}
              assessment={assessment}
              question={question}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

interface QuestionAccordianPanelProps {
  index: number;
  course: LearningRecordCourse;
  assessment: Assessment;
  question: Question;
}

const QuestionAccordianPanel: React.FC<QuestionAccordianPanelProps> = ({ index, course, assessment, question }) => {
  const courseContext = useContext(CourseContext);
  const [expanded, setExpanded] = useState(false);

  const [dimensions, setDimensions] = React.useState<{ height: number | string }>({
    height: 0,
  });

  const questionType = useMemo(() => {
    const type = _.get(question, "type");
    return QUESTION_TYPES.find((t) => t.id === type)?.value || "NA";
  }, [question]);

  const questionReport = useMemo(() => {
    const questions = _.get(courseContext.courseContentSession, "report.questions") || [];
    const questionItem = questions?.find((item) => item.id === question.id);

    return questionItem;
  }, [courseContext.courseContentSession]);

  const score = useMemo(() => {
    return ((questionReport?.score || 0) * 100).toFixed(0);
  }, [questionReport]);

  const answerType = useMemo(() => {
    return _.get(question, "type");
  }, [question]);

  const [expandedPanelRef, expandedPanelBounds] = useMeasure();
  const [collapsedPanelRef, collapsedPanelBounds] = useMeasure();

  useEffect(() => {
    let height: any = 0;
    if (expanded) {
      height = (expandedPanelBounds.height || 0) + 40;
    } else {
      height = (collapsedPanelBounds.height || 0) + 40;
    }

    setDimensions({
      height: height
    });

  }, [expandedPanelBounds, collapsedPanelBounds, expanded]);


  return (
    <Spring to={{ height: dimensions.height }} config={{
      friction: 14,
      tension: 120,
      bounce: 1,
    }}>
      {props => (
        <animated.div style={{ ...props }}
          className={cx(styles.questionAccordianPanel)}
        >
          <div ref={collapsedPanelRef} className={cx(styles.body, styles.collapsed, {
            [styles.show]: !expanded
          })} onClick={() => setExpanded(true)}>
            <div className={styles.index}>{index + 1}</div>
            <div className={styles.question}>
              <div className={styles.name}>
                {question.name}
              </div>
              <div className={styles.type}>
                Question Type: {questionType}
              </div>
            </div>
            <div className={styles.actions}>
              <button className={cx(styles.action, styles.answer, {
                [styles.correct]: questionReport?.correct,
              })}>
                <i className={questionReport?.correct ? "paperflite-tick-unselected" : "paperflite-close"}></i>
              </button>
              <button className={cx(styles.action, styles.dropdown, styles.expand)}>
                <i className="paperflite-dropdown"></i>
              </button>
            </div>
          </div>
          <div ref={expandedPanelRef} className={cx(styles.body, styles.expanded, {
            [styles.show]: expanded
          })}>
            <div className={styles.header} onClick={() => setExpanded(false)}>
              <div className={styles.index}>Question {index + 1}</div>
              {answerType === "text" &&
                <div className={styles.score}>score <b>{score}%</b></div>
              }
              <div className={styles.actions}>
                <button className={cx(styles.action, styles.collapse)}>
                  <i className="paperflite-dropdown"></i>
                </button>
              </div>
            </div>
            <QuestionReportContainer
              key={index}
              course={course}
              assessment={assessment}
              question={question}
              report={questionReport}
            />
          </div>
        </animated.div>
      )}
    </Spring>
  );
}

interface QuestionReportContainerProps {
  course: LearningRecordCourse;
  assessment: Assessment;
  question?: Question;
  report?: QuestionReport;
}

const QuestionReportContainer: React.FC<QuestionReportContainerProps> = ({
  course,
  assessment,
  question,
  report,
}) => {
  const name = useMemo(() => {
    return _.get(question, "name");
  }, [question]);

  const answerType = useMemo(() => {
    return _.get(question, "type");
  }, [question]);

  return (
    <div className={cx(styles.questionContainer, styles.questionReportContainer, styles[answerType || ""])}>
      <div className={styles.nameField}>{name}</div>

      <div className={styles.answerField}>
        {answerType === "multiple_choice" && (
          <MultipleChoiceQuestionReportContainer
            question={question as MultipleChoiceQuestion}
            questionReport={report}
          />
        )}

        {answerType === "true_false" && (
          <MultipleChoiceQuestionReportContainer
            question={question as MultipleChoiceQuestion}
            questionReport={report}
          />
        )}

        {answerType === "text" && (
          <TextReportAnswer
            question={question as TextQuestion}
            questionReport={report}
          />
        )}
      </div>
    </div>
  );
};

interface MultipleChoiceQuestionReportContainerProps {
  question: MultipleChoiceQuestion;
  questionReport?: QuestionReport;
}

const MultipleChoiceQuestionReportContainer: React.FC<MultipleChoiceQuestionReportContainerProps> = ({ question, questionReport }) => {

  const options = useMemo(() => {
    return _.get(question, "options") || [];
  }, [question]);


  return (
    <div className={cx(styles.multipleChoiceQuestionContainer, styles.report)}>
      <div className={styles.optionsContainer}>
        {_.map(options, (option, index) => {
          const correct = option.uuid === questionReport?.correctAnswer;
          const selected = option.uuid === questionReport?.answer;

          let icon = "paperflite-radio-button-unselected";
          if (selected) {
            icon = correct ? 'paperflite-tick-unselected' : 'paperflite-close';
          } else if (correct) {
            icon = 'paperflite-tick-unselected';
          }

          return (
            <div
              className={cx(styles.optionContainer, {
                [styles.selected]: selected,
                [styles.correct]: correct,
              })}
            >
              <Tooltip disabled={!(selected || correct)} content={correct ? "Correct Answer" : "Your Answer"} placement="top">
                <div className={cx(styles.radio, {
                  [styles.selectedRadio]: selected,
                })}
                >
                  <i className={icon}></i>
                </div>
              </Tooltip>
              <div className={styles.optionText}>
                <span>{option.name}</span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  );
};
interface TextAnswerReportProps {
  question: Question;
  questionReport?: QuestionReport;
}

const TextReportAnswer: React.FC<TextAnswerReportProps> = ({
  question,
  questionReport
}) => {

  return (
    <div className={cx(styles.textAnswerReport, styles.report)}>
      <div className={styles.answer}>
        <div className={styles.label}>Your Answer:</div>
        <div className={styles.value}>{questionReport?.answer || ""}</div>
      </div>

      <div className={styles.feedback}>
        <div className={styles.label}>Feedback:</div>
        <div className={styles.value}>{questionReport?.feedback || ""}</div>
      </div>
    </div>
  );
};